import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import toast from "react-hot-toast";

enum StatusCode {
  Unauthorized = 401,
  Forbidden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
  Conflict = 409,
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json, text/plain, */*",
  "Content-Type": "application/json; charset=utf-8",
  "Access-Control-Allow-Origin": "*",
  "X-Requested-With": "XMLHttpRequest",
};

const injectToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  try {
    const token = localStorage.getItem("authToken");

    if (token !== null && config?.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      config.headers["X-API-Key"] = process.env.REACT_APP_PENOMO_BACKEND_MASTER_API_KEY;
    }
    return config;
  } catch (error: unknown) {
    const { message } = error as Error;
    throw new Error(message);
  }
};

class Http {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    return this.instance ?? this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL: process.env.REACT_APP_PENOMO_BACKEND_API ?? "http://localhost:3000",
      timeout: 60000, // Set timeout to 1 minute
      headers,
      withCredentials: true, // Enable cross-origin authentication
    });

    http.interceptors.request.use(injectToken, (error) => Promise.reject(new Error(error.message)));

    http.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;
        return this.handleError(response);
      }
    );

    this.instance = http;
    return http;
  }

  request<T = any, R = AxiosResponse<T>>(config: InternalAxiosRequestConfig): Promise<R> {
    return this.http.request(config);
  }

  get<T = any, R = AxiosResponse<T>>(url: string, config?: InternalAxiosRequestConfig): Promise<R> {
    return this.http.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: InternalAxiosRequestConfig): Promise<R> {
    return this.http.post<T, R>(url, data, config);
  }

  patch<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: InternalAxiosRequestConfig): Promise<R> {
    return this.http.patch<T, R>(url, data, config);
  }

  put<T = any, R = AxiosResponse<T>>(url: string, data?: T, config?: InternalAxiosRequestConfig): Promise<R> {
    return this.http.put<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(url: string, config?: InternalAxiosRequestConfig): Promise<R> {
    return this.http.delete<T, R>(url, config);
  }

  private handleError(response: AxiosResponse) {
    const { status, data } = response;

    switch (status) {
      case StatusCode.InternalServerError:
        toast.error("Internal Server Error");
        break;
      case StatusCode.Forbidden:
      case StatusCode.Unauthorized:
        // Specific handling for critical endpoints
        if (response.config?.url?.includes("/investors/me/web3auth")) {
          return Promise.resolve(response);
        } else {
          localStorage.clear();
          window.location.href = "/";
        }
        break;
      case StatusCode.TooManyRequests:
        toast.error("Too many requests. Please try again later.");
        break;
      case StatusCode.Conflict:
        return Promise.resolve(response); // Resolve the promise for 409 Conflict
      default:
        if (data?.message) {
          toast.error(data.message);
        } else {
          toast.error("An unexpected error occurred");
        }
        break;
    }
    return Promise.reject(response);
  }
}

export const http = new Http();
